import type { ClientMessage, ServerMessage } from '@chessshooter/shared';
import { DEV_SERVER_PORT, PROD_WS_PATH } from './netConstants';

// The transport boundary: the session (session.ts) talks to a Transport and never to a socket, so
// tests drive it with a fake and a later binary protocol or a different carrier only touches this file.

export interface Transport {
  send(msg: ClientMessage): void;
  close(): void;
}

export interface TransportHandlers {
  onOpen(): void;
  onMessage(msg: ServerMessage): void;
  /** the WebSocket close code (1006 for a dropped connection, 4000+ for the server's own reasons) */
  onClose(code: number): void;
}

/** Opens a connection to `url`; the handlers hear about it from then on. */
export type Connect = (url: string, handlers: TransportHandlers) => Transport;

export const connectWebSocket: Connect = (url, handlers) => {
  const socket = new WebSocket(url);
  socket.addEventListener('open', () => handlers.onOpen());
  socket.addEventListener('message', (e) => {
    if (typeof e.data !== 'string') return;
    let msg: ServerMessage;
    try {
      msg = JSON.parse(e.data) as ServerMessage;
    } catch {
      return; // the server only speaks JSON; anything else is noise
    }
    handlers.onMessage(msg);
  });
  socket.addEventListener('close', (e) => handlers.onClose(e.code));
  // an error is always followed by close, which does the reporting
  socket.addEventListener('error', () => {});
  return {
    send: (msg) => {
      if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
    },
    close: () => socket.close(),
  };
};

/**
 * Dev aid: the same transport with every message delayed by `lagMs` in each direction, so rubber
 * banding and reconciliation can be watched on one machine (`?lag=150`). Order is preserved.
 */
export function withLag(connect: Connect, lagMs: number): Connect {
  return (url, handlers) => {
    const inner = connect(url, {
      onOpen: () => handlers.onOpen(),
      onMessage: (msg) => setTimeout(() => handlers.onMessage(msg), lagMs),
      onClose: (code) => setTimeout(() => handlers.onClose(code), lagMs),
    });
    return {
      send: (msg) => setTimeout(() => inner.send(msg), lagMs),
      close: () => inner.close(),
    };
  };
}

export interface LocationLike {
  protocol: string;
  hostname: string;
  host: string;
}

/**
 * Where the server is: VITE_WS_URL if set; in dev the server's default port on the same host (so a
 * phone in the WLAN reaches it through the host's address); in production `/ws` on the own origin,
 * which the reverse proxy forwards.
 */
export function resolveWsUrl(env: { VITE_WS_URL?: string; DEV: boolean }, loc: LocationLike): string {
  if (env.VITE_WS_URL) return env.VITE_WS_URL;
  if (env.DEV) return `ws://${loc.hostname}:${DEV_SERVER_PORT}`;
  return `${loc.protocol === 'https:' ? 'wss' : 'ws'}://${loc.host}${PROD_WS_PATH}`;
}

/** Where the session token lives: per tab, so two tabs on one machine are two players. */
export interface TokenStore {
  get(): string | null;
  set(token: string): void;
}

export const TOKEN_KEY = 'chessshooter.session';

export function browserTokenStore(): TokenStore {
  const storage = (): Storage | null => {
    try {
      return typeof sessionStorage === 'undefined' ? null : sessionStorage;
    } catch {
      return null; // blocked storage throws on access in some private modes
    }
  };
  return {
    get: () => {
      try {
        return storage()?.getItem(TOKEN_KEY) ?? null;
      } catch {
        return null;
      }
    },
    set: (token) => {
      try {
        storage()?.setItem(TOKEN_KEY, token);
      } catch {
        // without storage a reload simply starts a new session
      }
    },
  };
}

export function memoryTokenStore(initial: string | null = null): TokenStore {
  let token = initial;
  return { get: () => token, set: (t) => (token = t) };
}
